import React, { useContext, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import Tabs from "components/ui/tabs/Tabs";
import { useTranslation } from "react-i18next";

import { AuthContext } from "shared/context/auth-context";
import ProfileSettings from "features/settings/ProfileSettings";
import TeammateSettings from "features/settings/TeammateSettings";
import SubscriptionSettings from "features/settings/SubscriptionSettings";
import HelpSettings from "features/settings/HelpSettings";
// See: backend/docs/arvento-telemetry.md §11
import TelemetrySettings from "features/settings/TelemetrySettings";
import { PERMISSION_TYPES } from "@routebot/constants";
import { isInternationalTheme } from "config/ConfigUtils";
import { resolveTabIndexFromQuery } from "pages/shared/utility";
import { canOperateCustomerPortal } from "features/customer-portal/access";
import InviteCustomerContacts from "features/customer-portal/InviteCustomerContacts";

const AnalysisManagement = () => {
  const auth = useContext(AuthContext);
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const settingsPageTabs = useMemo(() => {
    let allTabs = [];

    allTabs.push({
      id: "profile",
      label: t("profileSettings"),
      content: <ProfileSettings></ProfileSettings>,
    });

    if (auth.permissions[PERMISSION_TYPES.ACCESS_TO_GIVE_PERMISSIONS] || auth.company?.isDemo) {
      allTabs.push({
        id: "teammates",
        label: t("teamMates"),
        content: <TeammateSettings></TeammateSettings>,
      });
    }

    if ((isInternationalTheme() && auth?.permissions[PERMISSION_TYPES.ACCESS_TO_BILLING]) || auth.company?.isDemo) {
      allTabs.push({
        id: "subscription",
        label: t("subscriptionSettings") || "Subscription",
        content: <SubscriptionSettings></SubscriptionSettings>,
      });
    }

    if (isInternationalTheme()) {
      allTabs.push({
        id: "help",
        label: t("helpSettings.tabLabel") || "Help & Support",
        content: <HelpSettings></HelpSettings>,
      });
    }

    // Telemetri ayarları — ACCESS_TO_TELEMETRY izni olan kullanıcılar görür.
    // Credentials henüz girilmemiş olabileceği için arventoConfig.enabled kontrolü YAPILMAZ
    // (ilk girişten önce de erişim lazım).
    if (auth?.permissions?.[PERMISSION_TYPES.ACCESS_TO_TELEMETRY] && !auth.company?.geotabConfig?.enabled) {
      allTabs.push({
        id: "telemetry",
        label: t("telemetrySettings"),
        content: <TelemetrySettings />,
      });
    }

    if (canOperateCustomerPortal(auth)) {
      allTabs.push({
        id: "customer-contacts",
        label: t("customerPortal.settingsTab"),
        content: <InviteCustomerContacts />,
      });
    }

    return allTabs;
  }, [auth?.client?.role, auth?.permissions, auth?.company?.features, auth?.company?.geotabConfig?.enabled, t]);

  const tabParam = searchParams.get("tab");
  const initialTabIndex = resolveTabIndexFromQuery(tabParam, settingsPageTabs);

  return (
    <React.Fragment>
      <Tabs tabs={settingsPageTabs} isVertical={true} id="settings-page" initialTabIndex={initialTabIndex} />
    </React.Fragment>
  );
};

export default AnalysisManagement;
